import React, { useState, useEffect, useMemo } from 'react';
import CustomLineChart from 'shared/charts/LineChart';
import CustomBarChart from 'shared/charts/BarChart';
import CustomPieChart from 'shared/charts/PieChart';
import { Calendar, Users, TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import { BASE_URL } from 'shared/config';
import ItemSelector from 'shared/ui/ItemSelector';

const API_BASE = `${BASE_URL}/cloudshop?path=`;
const COMPANY_ID = '57c09c3b3ce7d59d048b46c9';
const PARAMS = '&api=v3&timezone=21600';

const FinanceDashboard = () => {
  const [sources, setSources] = useState([]);
  const [staff, setStaff] = useState([]);
  const [financeData, setFinanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [selectedUser, setSelectedUser] = useState('all');
  const [selectedSources, setSelectedSources] = useState([]);
  const getMonthStart = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  };
  const getToday = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  };
  const [startDate, setStartDate] = useState(getMonthStart());
  const [endDate, setEndDate] = useState(getToday());
  const [groupBy, setGroupBy] = useState('day');
  // Table UI hover states must be declared before any conditional returns
  const [hoveredDebitCol, setHoveredDebitCol] = useState(null);
  const [hoveredCreditCol, setHoveredCreditCol] = useState(null);
  const [hoveredDebitRow, setHoveredDebitRow] = useState(null);
  const [hoveredCreditRow, setHoveredCreditRow] = useState(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Helpers
  const isIgnoredSource = (id) => parseInt(id) === 301;

  useEffect(() => {
    if (sources.length > 0) {
      fetchFinanceData();
    }
  }, [selectedUser, startDate, endDate, groupBy, sources]);
  // Local filter helper
  const isSourceAllowed = (id) => {
    if (isIgnoredSource(id)) return false;
    if (!selectedSources || selectedSources.length === 0) return true; // no selection => all
    return selectedSources.some(s => String(s.id) === String(id));
  };

  const sourceOptions = useMemo(() => {
    return sources
      .filter(s => !isIgnoredSource(s.id))
      .map(s => ({ id: String(s.id), name: s.title }));
  }, [sources]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      
      const [sourcesRes, staffRes] = await Promise.all([
        fetch(`${API_BASE}/data/${COMPANY_ID}/sources${PARAMS}`),
        fetch(`${API_BASE}/${COMPANY_ID}/staff${PARAMS}`)
      ]);

      const sourcesData = await sourcesRes.json();
      const staffData = await staffRes.json();

      if (sourcesData.status) {
        setSources(sourcesData.data);
      }
      if (staffData.status) {
        setStaff(staffData.data);
      }

      setLoading(false);
    } catch (err) {
      setError('Не удалось загрузить данные: ' + err.message);
      setLoading(false);
    }
  };

  const fetchFinanceData = async () => {
    try {
      const start = Math.floor(new Date(startDate).getTime() / 1000);
      const end = Math.floor(new Date(endDate + ' 23:59:59').getTime() / 1000);
      
      const userParam = selectedUser !== 'all' ? `&user_id=${selectedUser}` : '';
      const path = `/report/${COMPANY_ID}/finance?group=${groupBy}${userParam}&start=${start}&end=${end}`;
      const url = `${API_BASE}${encodeURIComponent(path)}${PARAMS}`;
      
      const res = await fetch(url);
      const data = await res.json();

      if (data.status) {
        setFinanceData(data.data);
      }
    } catch (err) {
      setError('Не удалось загрузить финансовые данные: ' + err.message);
    }
  };

  const getSourceTitle = (id) => {
    const source = sources.find(s => s.id === parseInt(id));
    return source ? source.title : `Источник ${id}`;
  };

  

  const getPeriodTimestamp = (_id) => {
    const { year, month, day } = _id;
    const m = month ? month - 1 : 0;
    const d = day ? day : 1;
    return new Date(year, m, d).getTime();
  };

  const formatPeriodLabel = (_id) => {
    const { year, month, day } = _id;
    if (groupBy === 'day') {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    } else if (groupBy === 'month') {
      const monthNames = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
      return `${monthNames[(month || 1) - 1]}`;
    }
    return `${year}`;
  };

  const getSortedFinanceData = () => {
    return [...financeData].sort((a, b) => getPeriodTimestamp(a._id) - getPeriodTimestamp(b._id));
  };

  const formatChartData = () => {
    const sorted = getSortedFinanceData();
    let runningBalance = 0;
    return sorted.map(item => {
      const { year, month, day } = item._id;
      let label = '';
      if (groupBy === 'day') {
        label = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      } else if (groupBy === 'month') {
        const monthNames = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];
        label = `${monthNames[month - 1]} ${year}`;
      } else {
        label = `${year}`;
      }

      const totalCredit = Object.entries(item.credit || {})
        .filter(([id]) => isSourceAllowed(id))
        .reduce((sum, [, val]) => sum + val, 0);
      const totalDebit = Object.entries(item.debit || {})
        .filter(([id]) => isSourceAllowed(id))
        .reduce((sum, [, val]) => sum + val, 0);
      
      console.log('totalCredit', totalCredit);
      console.log('totalDebit', totalDebit);
      const periodBalance = totalDebit - totalCredit;
      runningBalance += periodBalance;
      return {
        date: label,
        credit: totalCredit,
        debit: totalDebit,
        balance: periodBalance,
        balanceCumulative: runningBalance,
        // ...item
      };
    });
  };

  const calculateTotals = () => {
    let totalCredit = 0;
    let totalDebit = 0;
    const creditBySource = {};
    const debitBySource = {};

    financeData.forEach(item => {
      Object.entries(item.credit || {}).forEach(([id, amount]) => {
        if (!isSourceAllowed(id)) return;
        totalCredit += amount;
        creditBySource[id] = (creditBySource[id] || 0) + amount;
      });
      Object.entries(item.debit || {}).forEach(([id, amount]) => {
        if (!isSourceAllowed(id)) return;
        totalDebit += amount;
        debitBySource[id] = (debitBySource[id] || 0) + amount;
      });
    });

    return { totalCredit, totalDebit, creditBySource, debitBySource };
  };

  const getPieData = (dataBySource, type) => {
    return Object.entries(dataBySource)
      .map(([id, amount]) => ({
        name: getSourceTitle(id),
        value: amount,
        id
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  };

  const formatCurrency = (value) => {
    return `${Number(value || 0).toLocaleString('en-US')} сом`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Загрузка финансовых данных...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  const chartData = formatChartData();
  console.log('chartData', chartData);
  const { totalCredit, totalDebit, creditBySource, debitBySource } = calculateTotals();
  const balance = totalDebit - totalCredit;

  const COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
  const lineChartConfig = {
    dataKeys: ['debit', 'credit', 'balance', 'balanceCumulative'],
    labels: ['Доходы', 'Расходы', 'Баланс', 'Баланс с накоплением'],
    colors: ['#10b981', '#ef4444', '#3b82f6', '#6366f1']
  };
  const barChartConfig = {
    dataKeys: ['debit', 'credit'],
    labels: ['Доходы', 'Расходы'],
    colors: ['#10b981', '#ef4444']
  };

  // Build general table data (period columns x source rows) for debit and credit
  const periodsSorted = getSortedFinanceData().map(item => item._id);
  const periodKeys = Array.from(new Map(periodsSorted.map(p => [JSON.stringify(p), p])).values());
  const periodLabels = periodKeys.map(formatPeriodLabel);

  const collectSourceIds = (type) => {
    const set = new Set();
    financeData.forEach(item => {
      Object.keys(item[type] || {}).forEach(id => {
        if (isSourceAllowed(id)) set.add(id);
      });
    });
    return Array.from(set);
  };

  const buildMatrix = (type) => {
    const sourceIds = collectSourceIds(type);
    const rows = sourceIds.map(id => {
      const name = getSourceTitle(id);
      const values = periodKeys.map(p => {
        const hit = financeData.find(x => x._id.year === p.year && (x._id.month || 0) === (p.month || 0) && (x._id.day || 0) === (p.day || 0));
        const amount = hit && hit[type] ? (hit[type][id] || 0) : 0;
        return amount;
      });
      return { id, name, values };
    });
    return rows;
  };

  const debitMatrix = buildMatrix('debit');
  const creditMatrix = buildMatrix('credit');

  const sumColumn = (rows, colIndex) => rows.reduce((sum, row) => sum + (row.values[colIndex] || 0), 0);
  const debitColTotals = periodKeys.map((_, idx) => sumColumn(debitMatrix, idx));
  const creditColTotals = periodKeys.map((_, idx) => sumColumn(creditMatrix, idx));

  // Heatmap intensity helper (0..1)
  const intensity = (val, max) => {
    if (!max) return 0;
    const ratio = val / max;
    return Math.max(0.08, Math.min(1, ratio));
  };
  const debitColMax = periodKeys.map((_, idx) => Math.max(...debitMatrix.map(r => r.values[idx] || 0), 0));
  const creditColMax = periodKeys.map((_, idx) => Math.max(...creditMatrix.map(r => r.values[idx] || 0), 0));
  const debitRowMaxById = Object.fromEntries(debitMatrix.map(r => [r.id, Math.max(...r.values, 0)]));
  const creditRowMaxById = Object.fromEntries(creditMatrix.map(r => [r.id, Math.max(...r.values, 0)]));

  // Export to Excel (two sheets: Доходы, Расходы) with numeric cells
  const handleExportExcel = async () => {
    try {
      const XLSX = await import('xlsx');
      const header = ['Источник', ...periodLabels];

      const debitAoa = [header, ...debitMatrix.map(r => [r.name, ...r.values])];
      const creditAoa = [header, ...creditMatrix.map(r => [r.name, ...r.values])];
      const debitTotalsRow = ['Итого', ...debitColTotals];
      const creditTotalsRow = ['Итого', ...creditColTotals];
      debitAoa.push(debitTotalsRow);
      creditAoa.push(creditTotalsRow);

      const wb = XLSX.utils.book_new();
      const debitWs = XLSX.utils.aoa_to_sheet(debitAoa);
      const creditWs = XLSX.utils.aoa_to_sheet(creditAoa);

      // Apply number formats with thousand separators to numeric ranges (no decimals)
      const applyNumberFormat = (ws) => {
        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let R = range.s.r + 1; R <= range.e.r; R++) { // skip header row
          for (let C = range.s.c + 1; C <= range.e.c; C++) { // skip first column (names)
            const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
            const cell = ws[cellRef];
            if (cell && (typeof cell.v === 'number')) {
              cell.z = '#,##0';
            }
          }
        }
      };

      applyNumberFormat(debitWs);
      applyNumberFormat(creditWs);

      // Zebra striping with conditional formatting-like alternating row background isn't supported natively by sheet utils,
      // but we can set column widths and an autofilter to improve readability
      const setWidthsAndFilter = (ws) => {
        const range = XLSX.utils.decode_range(ws['!ref']);
        const cols = [];
        for (let C = range.s.c; C <= range.e.c; C++) {
          cols.push({ wch: C === range.s.c ? 28 : 14 });
        }
        ws['!cols'] = cols;
        // Set autofilter on header row
        ws['!autofilter'] = { ref: XLSX.utils.encode_range({ r: range.s.r, c: range.s.c }, { r: range.s.r, c: range.e.c }) };
      };

      setWidthsAndFilter(debitWs);
      setWidthsAndFilter(creditWs);

      XLSX.utils.book_append_sheet(wb, debitWs, 'Доходы');
      XLSX.utils.book_append_sheet(wb, creditWs, 'Расходы');
      XLSX.writeFile(wb, `finance_${groupBy}.xlsx`);
    } catch (e) {
      console.error('Excel export failed', e);
      alert('Не удалось экспортировать в Excel. Убедитесь, что пакет xlsx установлен.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Финансовый отчет</h1>

        {/* Фильтры */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Users className="inline w-4 h-4 mr-1" />
                Сотрудник
              </label>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Все сотрудники</option>
                {staff.map(member => (
                  <option key={member._id} value={member._id}>
                    {member.data.name} ({member.type})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="inline w-4 h-4 mr-1" />
                Дата начала
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="inline w-4 h-4 mr-1" />
                Дата окончания
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Группировка</label>
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="day">По дням</option>
                <option value="month">По месяцам</option>
                <option value="year">По годам</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Источники</label>
              <ItemSelector
                options={sourceOptions}
                selectedItems={selectedSources}
                onSelect={(item) => setSelectedSources(prev => [...prev, item])}
                onRemove={(id) => setSelectedSources(prev => prev.filter(i => i.id !== id))}
                placeholder="Все источники"
                searchPlaceholder="Поиск источника..."
                noItemsFoundText="Ничего не найдено"
                isMultiSelect
              />
            </div>
          </div>
        </div>

        {/* Сводные карточки */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Всего доходов</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(totalDebit)}
                </p>
              </div>
              <TrendingUp className="w-12 h-12 text-green-500 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Всего расходов</p>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(totalCredit)}
                </p>
              </div>
              <TrendingDown className="w-12 h-12 text-red-500 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Чистый баланс</p>
                <p className={`text-2xl font-bold ${balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(balance)}
                </p>
              </div>
              <DollarSign className="w-12 h-12 text-blue-500 opacity-20" />
            </div>
          </div>
        </div>

        {/* Графики */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* График тренда */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Финансовый тренд</h2>
            <CustomLineChart data={chartData} config={lineChartConfig} />
          </div>

          {/* Столбчатая диаграмма */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Доходы vs Расходы</h2>
            <CustomBarChart data={chartData} config={barChartConfig} />
          </div>
        </div>

        {/* Круговые диаграммы */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Доходы по источникам (топ 8)</h2>
            <CustomPieChart data={getPieData(debitBySource, 'debit')} config={{ colors: COLORS }} />
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Расходы по источникам (топ 8)</h2>
            <CustomPieChart data={getPieData(creditBySource, 'credit')} config={{ colors: COLORS }} />
          </div>
        </div>

        {/* Детальные таблицы */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Разбивка доходов</h2>
            <div className="overflow-y-auto max-h-96">
              <table className="min-w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Источник</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Сумма</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {Object.entries(debitBySource)
                    .sort(([, a], [, b]) => b - a)
                    .map(([id, amount]) => (
                      <tr key={id}>
                        <td className="px-4 py-2 text-sm text-gray-900">{getSourceTitle(id)}</td>
                        <td className="px-4 py-2 text-sm text-gray-900 text-right">
                          {formatCurrency(amount)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Разбивка расходов</h2>
            <div className="overflow-y-auto max-h-96">
              <table className="min-w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Источник</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Сумма</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {Object.entries(creditBySource)
                    .sort(([, a], [, b]) => b - a)
                    .map(([id, amount]) => (
                      <tr key={id}>
                        <td className="px-4 py-2 text-sm text-gray-900">{getSourceTitle(id)}</td>
                        <td className="px-4 py-2 text-sm text-gray-900 text-right">
                          {formatCurrency(amount)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Общие таблицы по источникам и периодам */}
        <div className="mt-6 grid grid-cols-1 gap-6">
          <div className="flex items-center justify-end">
            <button onClick={handleExportExcel} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Экспорт в Excel</button>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Доходы по источникам и периодам</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full border-separate ring-1 ring-gray-200 rounded-md" style={{ borderSpacing: 0 }}>
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide sticky left-0 bg-gray-100 z-10">Источник</th>
                    {periodLabels.map((label, idx) => (
                      <th
                        key={idx}
                        onMouseEnter={() => setHoveredDebitCol(idx)}
                        onMouseLeave={() => setHoveredDebitCol(null)}
                        className={`px-4 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wide whitespace-nowrap ${hoveredDebitCol === idx ? 'bg-emerald-50' : ''}`}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {debitMatrix.map((row, rowIdx) => (
                    <tr key={row.id} onMouseEnter={() => setHoveredDebitRow(row.id)} onMouseLeave={() => setHoveredDebitRow(null)} className={rowIdx % 2 ? 'bg-gray-50' : 'bg-white'}>
                      <td className={`px-4 py-2 text-sm text-gray-900 sticky left-0 z-10 ${hoveredDebitRow === row.id ? 'bg-emerald-50' : rowIdx % 2 ? 'bg-gray-50' : 'bg-white'}`}>{row.name}</td>
                      {row.values.map((val, idx) => {
                        const colMax = debitColMax[idx] || 0;
                        const colInten = intensity(val, colMax);
                        const colBg = `rgba(16, 185, 129, ${colInten})`;
                        const rowMax = debitRowMaxById[row.id] || 0;
                        const rowInten = intensity(val, rowMax);
                        const rowBg = `rgba(16, 185, 129, ${rowInten})`;
                        const total = debitColTotals[idx] || 1;
                        const pct = total ? (val / total) * 100 : 0;
                        const cellBg = hoveredDebitCol === idx ? colBg : hoveredDebitRow === row.id ? rowBg : undefined;
                        return (
                          <td
                            key={idx}
                            className={`px-4 py-2 text-sm text-gray-900 text-right transition-colors ${rowIdx % 2 ? 'bg-gray-50' : 'bg-white'}`}
                            style={{ backgroundColor: cellBg }}
                            title={`${pct.toFixed(1)}%`}
                          >
                            {formatCurrency(val)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {/* Totals row */}
                  <tr>
                    <td className="px-4 py-2 text-sm font-semibold text-gray-900 sticky left-0 bg-gray-100 z-10">Итого</td>
                    {debitColTotals.map((val, idx) => (
                      <td key={idx} className="px-4 py-2 text-sm font-semibold text-gray-900 text-right bg-gray-100">{formatCurrency(val)}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Расходы по источникам и периодам</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full border-separate ring-1 ring-gray-200 rounded-md" style={{ borderSpacing: 0 }}>
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide sticky left-0 bg-gray-100 z-10">Источник</th>
                    {periodLabels.map((label, idx) => (
                      <th
                        key={idx}
                        onMouseEnter={() => setHoveredCreditCol(idx)}
                        onMouseLeave={() => setHoveredCreditCol(null)}
                        className={`px-4 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wide whitespace-nowrap ${hoveredCreditCol === idx ? 'bg-rose-50' : ''}`}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {creditMatrix.map((row, rowIdx) => (
                    <tr key={row.id} onMouseEnter={() => setHoveredCreditRow(row.id)} onMouseLeave={() => setHoveredCreditRow(null)} className={rowIdx % 2 ? 'bg-gray-50' : 'bg-white'}>
                      <td className={`px-4 py-2 text-sm text-gray-900 sticky left-0 z-10 ${hoveredCreditRow === row.id ? 'bg-rose-50' : rowIdx % 2 ? 'bg-gray-50' : 'bg-white'}`}>{row.name}</td>
                      {row.values.map((val, idx) => {
                        const colMax = creditColMax[idx] || 0;
                        const colInten = intensity(val, colMax);
                        const colBg = `rgba(239, 68, 68, ${colInten})`;
                        const rowMax = creditRowMaxById[row.id] || 0;
                        const rowInten = intensity(val, rowMax);
                        const rowBg = `rgba(239, 68, 68, ${rowInten})`;
                        const total = creditColTotals[idx] || 1;
                        const pct = total ? (val / total) * 100 : 0;
                        const cellBg = hoveredCreditCol === idx ? colBg : hoveredCreditRow === row.id ? rowBg : undefined;
                        return (
                          <td
                            key={idx}
                            className={`px-4 py-2 text-sm text-gray-900 text-right transition-colors ${rowIdx % 2 ? 'bg-gray-50' : 'bg-white'}`}
                            style={{ backgroundColor: cellBg }}
                            title={`${pct.toFixed(1)}%`}
                          >
                            {formatCurrency(val)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {/* Totals row */}
                  <tr>
                    <td className="px-4 py-2 text-sm font-semibold text-gray-900 sticky left-0 bg-gray-100 z-10">Итого</td>
                    {creditColTotals.map((val, idx) => (
                      <td key={idx} className="px-4 py-2 text-sm font-semibold text-gray-900 text-right bg-gray-100">{formatCurrency(val)}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinanceDashboard;